import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import {
  getUserEHRLaunchProfile,
  getUserEHRLaunchProfileAuth0,
} from '@/pages/api/scribeAPIV2';
import { type RootState } from '@/store';
import { type UserEHRLaunchProfile } from '@/types';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { GET_USER_EHR_LAUNCH_PROFILES } from '@heidi/integrations/ehr/contracts';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useQueryKindeProperties } from '../useQueryKindeProperties';
import { useQueryUserForContext } from '../useQueryUser';

// THE CACHE IDENTITY MOVED AHEAD OF THIS HOOK (ARCFE-1098, task 3.11 D2), which
// is backwards from the usual order and is worth one line rather than a
// rediscovery. This hook is store-tethered (`react-redux` for the auth token
// and provider) and cannot carve; `useMutateDeleteIntegrationEhrLaunch`, which
// invalidates this identity, is otherwise headless and could. A package hook
// may not import `@/hooks/query/**`, so the constant single-sites in the door's
// `/contracts` and is re-exported here NAME-INTACT — the two other app writers
// that name it (`useMutateConnectIntegrationEhrLaunch`,
// `useMutateUpdateIntegrationEhrLaunch`) resolve unchanged.
//
// The TUPLE below is unchanged and deliberately stays here: its second element
// is `{ authProvider }`, read from the Rematch store, and a `/contracts` factory
// taking an app enum it cannot name would be a worse lie than a partial
// identity honestly labelled.
export { GET_USER_EHR_LAUNCH_PROFILES };

export function useQueryUserEHRLaunchProfiles<T = UserEHRLaunchProfile[]>(
  select?: (data: UserEHRLaunchProfile[]) => T
) {
  const { isAuthenticated } = useIsAuthenticated();
  const authProvider = useSelector((state: RootState) => state.auth.authProvider);

  const isAuth0Login =
    authProvider === AuthProvider.AUTH0 || authProvider === AuthProvider.AUTH0_DESKTOP;

  const { data: kindeProperties } = useQueryKindeProperties();
  const { data: user } = useQueryUserForContext((data) => data.kinde_user_id);

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- isAuth0Login is derived from authProvider
    queryKey: [GET_USER_EHR_LAUNCH_PROFILES, { authProvider }],
    enabled: isAuthenticated && (isAuth0Login || !!kindeProperties?.region) && !!user,
    select,
    queryFn: async () => {
      const { data } = isAuth0Login
        ? await getUserEHRLaunchProfileAuth0()
        : await getUserEHRLaunchProfile();
      return data?.user_ehr_launch_profiles ?? [];
    },
  });
}
